/**
 * qda's own sync loop — replaces yaqatw's src/service/cloud/fsSync.ts.
 *
 * The cloud is a straight mirror of qda's local `__documents__` tree: a local
 * file at `/{projectName}/__documents__/{docName}/{file}` syncs to the cloud
 * key `{projectName}/__documents__/{docName}/{file}` (the provider adds its
 * own "YaqatW-DATA" app root on top — Dropbox/GDrive/OneDrive/Nextcloud; S3
 * has no such prefix, the bucket itself is the boundary). So the cloud layout
 * is exactly what you see for the project in the File Explorer, minus the
 * leading slash.
 *
 * Scope: `__documents__` and `__shared__` (plus the top-level project registry —
 * see pushRegistry / fetchCloudProjects). `__shared__` holds the project-level
 * entity store (codes/categories/relationships/layers — see
 * src/entities/entityStore.ts); without mirroring it, shared entities would
 * silently not sync across devices. syncNow() never reads or writes anything
 * else — other per-project data (working-directory tab state, the plugin KV
 * file) stays local, and a real Word+yaqatw install's own cloud data (which
 * lives at `{projectName}/{fileId}/...`, a different subtree) is never touched.
 *
 * The loop is a generic diff over those subtrees: local-only files are uploaded,
 * remote-only files downloaded, and files present on both sides reconciled by
 * modified time (see syncOneFile). It carries whatever files exist under them —
 * today document.json + highlights.json + per-document entity parts under
 * `__documents__`, and the shared entity parts under `__shared__` — with no
 * hardcoded filename list.
 *
 * Encryption: whole-payload AES-GCM via the project's own key
 * (useProjectStore().projectKey — the same key src/data/fs.ts already uses
 * for all local storage), through the existing Cipher class
 * (src/data/crypto/cipher.ts). No new crypto code. Per-project-key means
 * this only round-trips between qda installs, not with a real Word+yaqatw
 * instance of the same project (see the plan's Context section for why
 * that trade-off was chosen).
 */
import { db } from "../data/db";
import { fs, SHARED_SEGMENT } from "../data/fs";
import { useProjectStore } from "../store/projectStore";
import { useTabsStore } from "../store/tabsStore";
import { Cipher } from "../data/crypto/cipher";
import { sync } from "./sync";
import { getSettingValue } from "../settings/settingsStore";
import { useSyncStatusStore } from "./syncStatusStore";
import { reloadActiveDocumentFromDisk } from "../editor/tabs";
import { cancelPendingAutosave } from "../editor/autosave";
import { listProjects } from "../data/projects";
import type { FileMetadata } from "./types";
import type { ProjectMeta } from "../data/projects";

interface CloudEnvelope {
  /** JSON-stringified payload; base64 AES-GCM ciphertext when `encrypted`. */
  content: string;
  /** Local mtime (epoch ms) at the moment this envelope was packed. */
  modified: number;
  size: number;
  encoding: "text";
  /** Cloud key this envelope was written to — self-describing, for debugging. */
  path: string;
  encrypted: boolean;
}

/**
 * The project subtrees qda syncs. Both the local fs path segments and the cloud
 * key segments — they're the same, since the cloud mirrors local paths.
 * `SHARED_SEGMENT` is imported from fs.ts so it can't drift from the path builder.
 */
const DOCUMENTS_SEGMENT = "__documents__";
const SYNC_SEGMENTS = [DOCUMENTS_SEGMENT, SHARED_SEGMENT];

/** True if a local key (e.g. "/proj/__shared__/x.json") is under a synced subtree. */
function isSyncedLocalKey(key: string, projectName: string): boolean {
  return SYNC_SEGMENTS.some((seg) => key.startsWith(`/${projectName}/${seg}/`));
}

/** True if a cloud key (lowercased) is under a synced subtree. */
function isSyncedCloudKey(lowerKey: string): boolean {
  return SYNC_SEGMENTS.some((seg) => lowerKey.includes(`/${seg.toLowerCase()}/`));
}

/**
 * Opt-in, per-file sync tracing. Off by default: syncNow runs on a timer and
 * log.ts warns about leaking data to production consoles, so this is not the
 * dev-only `log()`. Toggle via Settings > Backend > Advanced > Sync debug
 * logging; read fresh on every call so it applies live without a reload.
 */
function syncLog(...args: unknown[]): void {
  if (getSettingValue("backend", "syncDebugLogging") === true) console.log("[qdaSync]", ...args);
}

/**
 * Extract the document fileId from a cloud/local key of the form
 * `{project}/__documents__/{fileId}/{file}` (leading slash tolerated). The
 * folder segment is the fileId, not the display name (see src/data/fs.ts). Used
 * to tell which open document a downloaded file belongs to, so the editor can
 * be refreshed. Returns null if the key isn't inside a __documents__ subtree.
 */
function documentIdFromKey(key: string): string | null {
  const parts = key.split("/").filter(Boolean);
  const idx = parts.findIndex((p) => p.toLowerCase() === DOCUMENTS_SEGMENT.toLowerCase());
  return idx >= 0 && parts.length > idx + 1 ? parts[idx + 1] : null;
}

/**
 * Cross-project registry file, kept at the cloud app-root (a sibling of the
 * per-project folders, not inside any of them). It carries every project's
 * name/label/createdAt and password-wrapped key, so a device with an empty or
 * wiped IndexedDB can list and unlock these projects from the cloud alone —
 * without it, the random per-project data key (which lives only in the local
 * registry) would be unrecoverable and the cloud documents undecryptable.
 *
 * Stored unencrypted: the wrapped keys inside are already password-encrypted,
 * but project names/labels are visible to anyone with cloud access — a
 * deliberate trade-off for cross-device recovery.
 */
const REGISTRY_CLOUD_KEY = "__projects__/index.json";

interface RegistryEnvelope {
  entries: ProjectMeta[];
  modified: number;
}

/**
 * Upload the whole local project registry to the cloud. Called from syncNow so
 * every sync keeps the cloud registry current; non-fatal on failure.
 */
export async function pushRegistry(): Promise<void> {
  try {
    const entries = await listProjects();
    const envelope: RegistryEnvelope = { entries, modified: Date.now() };
    await sync.pushRaw(REGISTRY_CLOUD_KEY, envelope);
  } catch (err) {
    console.warn("[qdaSync] Could not push project registry:", err);
  }
}

/**
 * Fetch the list of projects recorded in the cloud registry — name, label and
 * password-wrapped key for each. Meant for the projects home screen (no project
 * unlocked yet) to offer them as choices: selecting one and entering its
 * password unwraps the stored key (see projects.importProject), recovering the
 * exact data key its cloud documents were encrypted with.
 *
 * Only entries that actually carry a wrapped key are returned — a name on its
 * own can't decrypt anything, so it would be a dead choice. Does NOT write
 * anything locally; the caller adopts a project only when the user picks it.
 * Throws if the provider can't be reached, so the caller decides how loud to be.
 */
export async function fetchCloudProjects(): Promise<ProjectMeta[]> {
  const payload = (await sync.pullRaw(REGISTRY_CLOUD_KEY)) as RegistryEnvelope | null;
  const entries = payload && Array.isArray(payload.entries) ? payload.entries : [];
  return entries.filter(
    (p): p is ProjectMeta =>
      !!p && typeof p.name === "string" && typeof p.wrappedKey === "string" && !!p.wrappedKey
  );
}

function requireProjectKey(): CryptoKey {
  const projectKey = useProjectStore.getState().projectKey;
  if (!projectKey) {
    throw new Error("Project is locked — cannot sync without an unlocked project key.");
  }
  return projectKey;
}

async function bytesToBase64(bytes: Uint8Array): Promise<string> {
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk) as any);
  }
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function packEnvelope(cloudKey: string, data: unknown, localModifiedMs: number): Promise<CloudEnvelope> {
  const json = JSON.stringify(data);
  const projectKey = requireProjectKey();
  const encrypted = await Cipher.encryptWithKey(json, projectKey);
  const content = await bytesToBase64(encrypted);
  return { content, modified: localModifiedMs, size: content.length, encoding: "text", path: cloudKey, encrypted: true };
}

async function unpackEnvelope(envelope: CloudEnvelope): Promise<unknown> {
  if (!envelope.encrypted) return JSON.parse(envelope.content);
  const projectKey = requireProjectKey();
  const json = await Cipher.decryptWithKey(base64ToBytes(envelope.content), projectKey);
  return JSON.parse(json);
}

/**
 * AES-GCM decrypt/encrypt failures surface as a DOMException named
 * "OperationError" — for decrypt it means the ciphertext wasn't produced by
 * this project's key (e.g. the cloud copy was encrypted under a different
 * project password/key, as happens when the "same" project was created
 * independently on two devices instead of opened from the cloud).
 */
function isCryptoKeyMismatch(err: unknown): boolean {
  return err instanceof DOMException && err.name === "OperationError";
}

type FileSyncResult = "uploaded" | "downloaded" | "unchanged" | "skipped";

/**
 * Diffs one local file against its known cloud counterpart and pushes
 * whichever side is newer (or the only side that exists). Mirrors yaqatw's
 * fsSync.syncBackend per-file logic: second-precision comparison, tolerating
 * timestamp-granularity differences across providers.
 */
async function syncOneFile(
  localPath: string,
  cloudKey: string,
  remote: FileMetadata | undefined
): Promise<FileSyncResult> {
  const localExists = await fs.exists(localPath);
  const localStat = localExists ? await fs.stat(localPath) : null;
  const localModifiedMs = localStat ? new Date(localStat.modified).getTime() : 0;
  const remoteModifiedMs = remote ? new Date(remote.modifiedTime).getTime() : 0;

  if (!localExists && !remote) return "skipped";

  const localSec = Math.floor(localModifiedMs / 1000);
  const remoteSec = Math.floor(remoteModifiedMs / 1000);

  syncLog(
    cloudKey,
    `local=${localExists ? `${localStat?.modified}(${localSec})` : "none"}`,
    `remote=${remote ? `${remote.modifiedTime}(${remoteSec})` : "none"}`
  );

  if (localExists && (!remote || localSec > remoteSec)) {
    let envelope: CloudEnvelope;
    try {
      const data = await fs.readFile(localPath);
      envelope = await packEnvelope(cloudKey, data, localModifiedMs);
    } catch (err) {
      if (isCryptoKeyMismatch(err)) {
        syncLog(cloudKey, "upload skipped — local copy can't be decrypted with the current project key");
        console.warn(
          `[qdaSync] Skipping upload of ${cloudKey}: the local copy was encrypted with a different project key than the one currently unlocked.`
        );
        return "skipped";
      }
      throw err;
    }
    const pushed = (await sync.pushRaw(cloudKey, envelope)) as { modifiedTime?: string } | undefined;
    // Restamp the local file's mtime to the SERVER's own modified time, exactly
    // as the download branch does. Without this, a locally-edited file keeps
    // this device's wall-clock mtime while everything downloaded carries
    // server-clock mtime — so if this device's clock runs ahead of the server,
    // the file looks permanently "newer" than the cloud: a genuinely newer
    // remote copy never downloads, and this stale copy re-uploads over it every
    // cycle. Re-stamp via the stored value (not a re-encrypt of `data`) so a
    // concurrent autosave that landed during the push isn't reverted.
    if (pushed?.modifiedTime) {
      const current = await db.getItem(localPath);
      if (current) await db.setItem(localPath, current.value, pushed.modifiedTime);
      syncLog(cloudKey, `uploaded → restamped local mtime to ${pushed.modifiedTime}`);
    } else {
      syncLog(cloudKey, "uploaded (provider returned no modifiedTime; local mtime unchanged)");
    }
    return "uploaded";
  }

  if (remote && (!localExists || remoteSec > localSec)) {
    const envelope = (await sync.pullRaw(cloudKey, remote.id)) as CloudEnvelope | null;
    if (!envelope) {
      syncLog(cloudKey, "download skipped — provider returned no payload");
      return "skipped";
    }
    let data: unknown;
    try {
      data = await unpackEnvelope(envelope);
    } catch (err) {
      if (isCryptoKeyMismatch(err)) {
        syncLog(cloudKey, "download skipped — cloud copy encrypted with a different project password/key");
        console.warn(
          `[qdaSync] Skipping download of ${cloudKey}: the cloud copy was encrypted with a different project password/key and can't be decrypted on this device. Open this project via "Open from cloud" with the matching password to reconcile.`
        );
        return "skipped";
      }
      throw err;
    }
    // Stamp local mtime to the remote's own modifiedTime (see fs.ts's
    // writeFile doc comment) so the next sync pass doesn't see this write's
    // "now" as newer-than-remote and immediately re-upload it.
    await fs.writeFile(localPath, data, remote.modifiedTime);
    syncLog(cloudKey, `downloaded → local overwritten (mtime ${remote.modifiedTime})`);
    return "downloaded";
  }

  syncLog(cloudKey, "unchanged");
  return "unchanged";
}

/**
 * Syncs every document in the active project (matches yaqatw's syncAll
 * whole-project scope, not just the active tab). Called by the "Sync now"
 * settings action and by the sync-interval effect in src/App.tsx.
 *
 * `resetAuthGate` mirrors yaqatw's own `Sync.syncNow()`, which always clears
 * BaseCloudProvider's authBlocked gate before syncing — a provider that
 * failed to authenticate twice in a row (e.g. no OAuth client ID configured)
 * otherwise stays permanently blocked, replaying the same stale "sign-in is
 * paused" error forever, even after the user fixes the underlying problem
 * and clicks "Sync now" again. Only the explicit "Sync now" button passes
 * `true` — the automatic interval tick in src/App.tsx deliberately doesn't,
 * since resetting there would just re-attempt an OAuth popup with no user
 * gesture behind it (browsers block that) and spam the console every tick
 * instead of failing once and staying quiet until the user acts.
 */
export async function syncNow(opts: { resetAuthGate?: boolean } = {}): Promise<void> {
  const statusStore = useSyncStatusStore.getState();
  const { projectName, projectKey } = useProjectStore.getState();

  if (!projectName) {
    statusStore.setStatus("error", "No project open — cannot sync.");
    return;
  }
  const cloudServer = getSettingValue("backend", "cloudServer");
  if (!cloudServer) {
    statusStore.setStatus("error", "No cloud backend configured. Set one in Settings > Backend.");
    return;
  }
  if (!projectKey) {
    statusStore.setStatus("error", "Project is locked — cannot sync without an unlocked project key.");
    return;
  }

  if (opts.resetAuthGate) {
    try {
      (await sync.ensureProvider()).resetAuthGate?.();
    } catch (err) {
      console.warn("[qdaSync] Could not reset auth gate before syncing:", err);
    }
  }

  statusStore.setStatus("syncing", "Syncing…");
  // Keep the cloud copy of the project registry current so other devices can
  // discover and unlock this project (see fetchCloudProjects). Runs
  // alongside the document sync, not gating it.
  void pushRegistry();
  try {
    // Local side: every stored file under this project's synced subtrees
    // (__documents__ + __shared__). The cloud key is just the local path minus
    // its leading slash.
    const localKeys = (await db.getKeys()).filter((k) => isSyncedLocalKey(k, projectName));

    // Remote side: one listing for the whole project — every provider's
    // getMetadataAll does a single recursive walk, so this is cheap compared
    // to a per-file existence check. Keep only files inside the synced subtrees;
    // anything else (yaqatw's own {fileId}/... Word data) is never touched.
    let remoteFiles: FileMetadata[] = [];
    try {
      remoteFiles = await sync.getMetadataAll(`${projectName}/`);
    } catch (err) {
      console.warn("[qdaSync] Could not list remote files (continuing as if empty):", err);
    }
    const remoteByKey = new Map<string, FileMetadata>();
    for (const f of remoteFiles) {
      const key = f.path.toLowerCase();
      if (isSyncedCloudKey(key)) remoteByKey.set(key, f);
    }
    syncLog(
      `remote listing for "${projectName}": ${remoteFiles.length} total, ` +
        `${remoteByKey.size} under ${SYNC_SEGMENTS.join("/")}`,
      Array.from(remoteByKey.keys())
    );

    // Union of both sides, keyed by lowercased cloud key so a local file and
    // its remote counterpart collapse to one target (providers lowercase keys
    // on write). This uploads local-only files, downloads remote-only ones,
    // and reconciles the rest by mtime inside syncOneFile.
    const targets = new Map<string, { localPath: string; cloudKey: string }>();
    for (const key of localKeys) {
      const cloudKey = key.slice(1); // drop leading "/"
      targets.set(cloudKey.toLowerCase(), { localPath: key, cloudKey });
    }
    for (const [lowerKey, meta] of remoteByKey) {
      if (!targets.has(lowerKey)) {
        targets.set(lowerKey, { localPath: `/${meta.path}`, cloudKey: meta.path });
      }
    }
    syncLog(
      `local keys: ${localKeys.length}, union targets: ${targets.size}`,
      localKeys.map((k) => k.slice(1).toLowerCase())
    );

    if (targets.size === 0) {
      statusStore.setStatus("success", "Nothing to sync — this project has no documents yet.");
      return;
    }

    let uploaded = 0;
    let downloaded = 0;
    let failed = 0;
    // fileIds that had at least one file replaced by a newer remote copy — the
    // open one among these needs its editor refreshed.
    const downloadedFileIds = new Set<string>();

    for (const { localPath, cloudKey } of targets.values()) {
      try {
        const result = await syncOneFile(localPath, cloudKey, remoteByKey.get(cloudKey.toLowerCase()));
        if (result === "uploaded") uploaded++;
        else if (result === "downloaded") {
          downloaded++;
          const fileId = documentIdFromKey(cloudKey);
          if (fileId) downloadedFileIds.add(fileId.toLowerCase());
        }
      } catch (err) {
        failed++;
        console.warn(`[qdaSync] Failed to sync ${cloudKey}:`, err);
      }
    }

    // If the currently open document was one of the downloads, its on-disk copy
    // now differs from what the editor holds in memory. Reload it so the user
    // sees the newer remote content — and so the next edit doesn't save the
    // stale in-memory copy back over it.
    if (downloaded > 0) {
      const activeId = useTabsStore.getState().activeId;
      if (activeId && downloadedFileIds.has(activeId.toLowerCase())) {
        try {
          await reloadActiveDocumentFromDisk(projectName, activeId);
        } catch (err) {
          console.warn("[qdaSync] Could not reload active document after download:", err);
        }
      }
    }

    const summary = `Synced: ${uploaded} uploaded, ${downloaded} downloaded${failed ? `, ${failed} failed` : ""}.`;
    syncLog(summary);
    statusStore.setStatus(failed > 0 && uploaded + downloaded === 0 ? "error" : "success", summary);
  } catch (err: any) {
    console.error("[qdaSync] syncNow failed:", err);
    statusStore.setStatus("error", `Sync failed: ${err?.message || String(err)}`);
  }
}

/**
 * Force the cloud copy to win for the active project, ignoring the modified-time
 * comparison entirely: every remote file under the project's __documents__ tree
 * is downloaded and overwrites the local copy. The manual "cloud wins" override
 * behind Settings > Backend > "Pull cloud version".
 *
 * Why it exists: sync's normal newer-wins rule can get wedged when a local file
 * carries a modified time that only *looks* newer than the cloud — e.g. a copy
 * written by an older build before mtimes were stamped from server time, or a
 * device whose clock ran ahead. Those stale timestamps aren't self-healing (a
 * device that only syncs, never edits, never re-uploads to correct them), so
 * this re-seeds the device from the authoritative cloud state in one action.
 *
 * Only pulls: local-only files (documents that exist here but not in the cloud)
 * are left untouched, and nothing is uploaded. Overwrites this device's copies
 * of everything that IS in the cloud, including any unsynced local edits to
 * those documents — that's the point of "cloud wins".
 */
export async function pullFromCloud(): Promise<void> {
  const statusStore = useSyncStatusStore.getState();
  const { projectName, projectKey } = useProjectStore.getState();

  if (!projectName) {
    statusStore.setStatus("error", "No project open — cannot pull.");
    throw new Error("No project open — cannot pull.");
  }
  const cloudServer = getSettingValue("backend", "cloudServer");
  if (!cloudServer) {
    statusStore.setStatus("error", "No cloud backend configured. Set one in Settings > Backend.");
    throw new Error("No cloud backend configured.");
  }
  if (!projectKey) {
    statusStore.setStatus("error", "Project is locked — cannot pull without an unlocked project key.");
    throw new Error("Project is locked — cannot pull without an unlocked project key.");
  }

  // Manual action, like syncNow — clear a stale auth-blocked gate so a retry
  // after fixing credentials actually re-attempts sign-in.
  try {
    (await sync.ensureProvider()).resetAuthGate?.();
  } catch (err) {
    console.warn("[qdaSync] Could not reset auth gate before pulling:", err);
  }

  // Drop any queued local save up front so an in-flight autosave can't land on
  // disk mid-pull and get read back as the "pulled" content for the open doc.
  cancelPendingAutosave();

  statusStore.setStatus("syncing", "Pulling cloud copy…");
  try {
    const remoteFiles = await sync.getMetadataAll(`${projectName}/`);
    const docFiles = remoteFiles.filter((f) => isSyncedCloudKey(f.path.toLowerCase()));

    // Map each remote file onto the existing local key when there is one, so we
    // overwrite it in place rather than writing a lowercased duplicate (remote
    // keys are lowercased on write; local keys keep their original case).
    const localByLower = new Map<string, string>();
    for (const k of (await db.getKeys()).filter((k) => isSyncedLocalKey(k, projectName))) {
      localByLower.set(k.slice(1).toLowerCase(), k);
    }

    syncLog(`pullFromCloud "${projectName}": ${docFiles.length} remote files under ${SYNC_SEGMENTS.join("/")}`);

    if (docFiles.length === 0) {
      statusStore.setStatus("success", "Nothing in the cloud to pull for this project.");
      return;
    }

    let downloaded = 0;
    let failed = 0;
    const downloadedFileIds = new Set<string>();

    for (const remote of docFiles) {
      const cloudKey = remote.path;
      const localPath = localByLower.get(cloudKey.toLowerCase()) ?? `/${cloudKey}`;
      try {
        const envelope = (await sync.pullRaw(cloudKey, remote.id)) as CloudEnvelope | null;
        if (!envelope) {
          syncLog(cloudKey, "pull skipped — provider returned no payload");
          continue;
        }
        const data = await unpackEnvelope(envelope);
        await fs.writeFile(localPath, data, remote.modifiedTime);
        downloaded++;
        const fileId = documentIdFromKey(cloudKey);
        if (fileId) downloadedFileIds.add(fileId.toLowerCase());
        syncLog(cloudKey, `pulled → local overwritten (mtime ${remote.modifiedTime})`);
      } catch (err) {
        failed++;
        console.warn(`[qdaSync] Failed to pull ${cloudKey}:`, err);
      }
    }

    // Refresh the open editor if its document was among those replaced.
    if (downloaded > 0) {
      const activeId = useTabsStore.getState().activeId;
      if (activeId && downloadedFileIds.has(activeId.toLowerCase())) {
        try {
          await reloadActiveDocumentFromDisk(projectName, activeId);
        } catch (err) {
          console.warn("[qdaSync] Could not reload active document after pull:", err);
        }
      }
    }

    const summary = `Pulled cloud copy: ${downloaded} downloaded${failed ? `, ${failed} failed` : ""}.`;
    syncLog(summary);
    if (failed > 0 && downloaded === 0) {
      statusStore.setStatus("error", summary);
      throw new Error(summary);
    }
    statusStore.setStatus("success", summary);
  } catch (err: any) {
    console.error("[qdaSync] pullFromCloud failed:", err);
    statusStore.setStatus("error", `Pull failed: ${err?.message || String(err)}`);
    throw err;
  }
}

export const qdaSync = { syncNow, pushRegistry, fetchCloudProjects, pullFromCloud };
export default qdaSync;
